import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { IconComponent } from '@progress/kendo-angular-icons';
import { TextAreaComponent } from '@progress/kendo-angular-inputs';
import { finalize, map } from 'rxjs';
import { FileDropzoneComponent } from 'src/app/features/file-dropzone/file-dropzone.component';
import { confirmationModalOpener } from 'src/app/features/modals/confirmation-modal/confirmation-modal.modal';
import { FeedbackCategory } from 'src/app/models/feedback/feedback-category';
import { ModalConfig } from 'src/app/models/modal-config';
import { FeedbackService } from 'src/app/services/api/user-api';
import { ModalOpenerService } from 'src/app/services/modal-opener.service';
import { FEEDBACK_MODAL_DATA } from './feedback-modal.modal';

interface CategoryOption {
  readonly value: FeedbackCategory;
  readonly icon: string;
  readonly text: string;
  readonly subtext: string;
  readonly hint: string;
}

@Component({
  selector: 'app-feedback-modal',
  templateUrl: './feedback-modal.component.html',
  styleUrl: './feedback-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    FileDropzoneComponent,
    IconComponent,
    TextAreaComponent,
    ButtonComponent
  ]
})
export class FeedbackModalComponent {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly modalOpener = inject(ModalOpenerService);
  private readonly feedbackService = inject(FeedbackService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly inputData = inject(FEEDBACK_MODAL_DATA);

  protected readonly categoryOptions: readonly CategoryOption[] = [
    {
      value: FeedbackCategory.Application,
      icon: 'clipboard',
      text: 'The PMT application',
      subtext: 'How it works and looks',
      hint: 'What works well, what is confusing, or what would you add or change?'
    },
    {
      value: FeedbackCategory.ProcessTaskChecklist,
      icon: 'share',
      text: 'A process, task or checklist',
      subtext: 'Something to change, add or remove',
      hint: 'Which process, task or checklist is it, and what should be different?'
    },
    {
      value: FeedbackCategory.Other,
      icon: 'comment',
      text: 'Something else',
      subtext: 'Not sure? Choose this one',
      hint: "Tell us what's on your mind."
    }
  ];

  protected readonly maxTextLength = 2000;
  protected readonly maxScreenshots = 5;
  protected readonly maxScreenshotSize = 5 * 1024 * 1024;
  protected readonly screenshotExtensions = ['.png', '.jpg', '.jpeg'];

  protected readonly form = new FormGroup({
    category: new FormControl<FeedbackCategory | null>(null, {
      validators: [Validators.required]
    }),
    text: new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.pattern(/\S/),
        Validators.maxLength(this.maxTextLength)
      ]
    }),
    screenshots: new FormControl<File[]>([], { nonNullable: true })
  });

  protected readonly isValid = toSignal(
    this.form.statusChanges.pipe(map(() => this.form.valid)),
    { initialValue: this.form.valid }
  );
  private readonly category = toSignal(
    this.form.controls.category.valueChanges,
    { initialValue: null }
  );
  protected readonly selectedOption = computed(() =>
    this.categoryOptions.find(option => option.value === this.category())
  );
  protected readonly textLength = toSignal(
    this.form.controls.text.valueChanges.pipe(map(text => text.length)),
    { initialValue: 0 }
  );
  protected readonly submitting = signal(false);
  protected readonly submitFailed = signal(false);
  protected readonly errorDismissed = signal(false);
  protected readonly failureCount = signal(0);
  protected readonly serverMessage = signal<string | null>(null);
  protected readonly sent = signal(false);

  protected submit(): void {
    if (this.form.invalid || this.submitting()) {
      return;
    }

    const { category, text, screenshots } = this.form.getRawValue();
    this.submitting.set(true);
    this.submitFailed.set(false);
    this.errorDismissed.set(false);
    this.serverMessage.set(null);
    this.feedbackService
      .create$({ category, text: text.trim() }, screenshots)
      .pipe(
        finalize(() => this.submitting.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => this.sent.set(true),
        error: (error: HttpErrorResponse) => {
          this.failureCount.update(count => count + 1);
          this.serverMessage.set(
            error.status === 400 && typeof error.error === 'string'
              ? error.error
              : null
          );
          this.submitFailed.set(true);
        }
      });
  }

  protected sendAnother(): void {
    this.form.reset();
    this.failureCount.set(0);
    this.sent.set(false);
  }

  protected cancel(): void {
    if (this.sent()) {
      this.activeModal.close();
      return;
    }
    if (!this.hasUserInput()) {
      this.activeModal.dismiss();
      return;
    }

    this.modalOpener
      .open(confirmationModalOpener, {
        modalConfig: new ModalConfig(
          "It hasn't been sent yet.",
          'Discard',
          'Discard feedback?',
          'Keep editing',
          false,
          true
        )
      })
      .closed$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.activeModal.dismiss());
  }

  private hasUserInput(): boolean {
    const { category, text, screenshots } = this.form.getRawValue();
    return category !== null || text.trim() !== '' || screenshots.length > 0;
  }
}
